/**
 * Where a dragged tile lands, and the copy of it that follows the pointer.
 * `./tile-drag.ts` is the dragging.
 */

/** How far outside the line a tile can still be dropped on it, in pixels. */
const reach = 24;

/** A point on the page. */
export interface Point {
  readonly x: number;
  readonly y: number;
}

/** What a tile's box says about where a point is. */
type Box = Pick<DOMRect, "left" | "right" | "top" | "bottom">;

/**
 * Which of the line's tiles a point drops in front of, or nothing for the end.
 *
 * The line wraps, so a point is in front of the first tile whose row is below
 * it, or which shares its row and is centred to its right.
 * @param point - Where the pointer is.
 * @param boxes - The tiles on the line, in order.
 * @returns The index of the tile to drop in front of, or the length for the end.
 */
export function dropIndex(point: Point, boxes: readonly Box[]): number {
  const found = boxes.findIndex(
    (box) =>
      point.y < box.top ||
      (point.y <= box.bottom && point.x < (box.left + box.right) / 2),
  );

  return found === -1 ? boxes.length : found;
}

/** Whether a point is on the line, or near enough to it. */
export function overLine(point: Point, box: Box): boolean {
  return (
    point.x >= box.left - reach &&
    point.x <= box.right + reach &&
    point.y >= box.top - reach &&
    point.y <= box.bottom + reach
  );
}

/** Where a pointer event happened. */
export function at(event: PointerEvent): Point {
  return { x: event.clientX, y: event.clientY };
}

/** The ghost that follows the pointer, a copy of the tile. */
export function ghostOf(tile: HTMLElement, point: Point): HTMLElement {
  const ghost = tile.cloneNode(true) as HTMLElement;
  const box = tile.getBoundingClientRect();

  ghost.removeAttribute("data-cb-fuxi-tile");
  ghost.removeAttribute("data-cb-fuxi-placed");
  ghost.classList.add("cb-fuxi-tile-ghost");
  ghost.setAttribute("aria-hidden", "true");
  ghost.style.setProperty("--cb-ghost-width", `${String(box.width)}px`);
  tile.ownerDocument.body.append(ghost);
  moveGhost(ghost, point);

  return ghost;
}

/** Put the ghost under the pointer. */
export function moveGhost(ghost: HTMLElement, point: Point): void {
  ghost.style.left = `${String(point.x)}px`;
  ghost.style.top = `${String(point.y)}px`;
}
